import React, { useState, useEffect } from 'react';
import propTypes from 'prop-types';
import { Box, Stack, styled, Table, TableCell, TableContainer, TableHead, Typography } from '@mui/material';
import Logger from '../../../lib/Logger';
import { renderErrorMessage, renderSuccessMessage } from '../../../lib/toastMessage';
import { LoadingButton } from '@mui/lab';

// import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';

// import { CustomMenu } from 'src/components/menu/customMenu';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
// import { axiosVBGetCall } from '../../../services/vbApiCall';
import { axiosGetCall, axiosPostCall } from 'src/services/apisCall';
import { PaginationTable } from './PaginationTable';
import { assign, set } from 'lodash';
import CustomModal from 'src/components/modal/customModal';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import FormProvider, { RHFTextField } from 'src/components/hook-form';

const BasicDatePicker = ({ value, onChange }) => {
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        slotProps={{
          textField: { size: 'small' },
          actionBar: {
            actions: ['today', 'clear'],
          },
        }}
        sx={{ width: '270px', margin: '8px' }}
        value={value}
        onChange={(newValue) => onChange(newValue)}
      />
    </LocalizationProvider>
  );
};

BasicDatePicker.propTypes = {
  value: propTypes.number,
  onChange: propTypes.func.isRequired,
};

const Row = styled(Stack)(() => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '16px',
  width: '100%',
}));

const FieldHeading = styled(Typography)(() => ({
  fontWeight: 600,
  minWidth: 260,
}));

const columns = [
  { id: 'plan_id', label: 'Plan ID', minWidth: 70, align: 'center' },
  { id: 'country_code', label: 'Country Code', minWidth: 70, align: 'center' },
  { id: 'title', label: 'Title', minWidth: 150, align: 'center' },
  { id: "deduction_type", label: "Deduction Type", minWidth: 70, align: 'center' },
  { id: 'single_part_english_char_limit', label: 'Single English Char', minWidth: 100, align: 'center' },
  { id: 'single_part_unicode_char_limit', label: 'Single Unicode Char', minWidth: 100, align: 'center' },
  { id: 'multi_part_english_char_limit', label: 'Multi English Char', minWidth: 100, align: 'center' },
  { id: 'multi_part_unicode_char_limit', label: 'Multi Unicode Char', minWidth: 100, align: 'center' },
  { id: 'transactional_sms_price', label: 'Trans', minWidth: 70, align: 'center' },
  { id: 'promotional_sms_price', label: 'Promo', minWidth: 70, align: 'center' },
  { id: 'explicit_sms_price', label: 'Explicit', minWidth: 70, align: 'center' },
  { id: 'implicit_sms_price', label: 'Implicit', minWidth: 70, align: 'center' },
  { id: 'dlt_sms_price', label: 'DLT', minWidth: 70, align: 'center' },
  { id: 'reseller_over_selling_x', label: 'Reseller', minWidth: 70, align: 'center' },
  { id: 'create_date', label: 'Create Date', minWidth: 100, align: 'center' },
  { id: 'actions', label: 'Action', minWidth: 80, align: 'center' },
];

export const SmsResellerPlanTable = ({ credits = [], assignedPlan = [], user = {}, setForceLoad, loading: tableLoading }) => {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [assignUser, setAssignUser] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const logger = new Logger('senderId');


  const Schema = Yup.object().shape({
    singleEngPercent: Yup.number().required("single eng percent is required"),
    singleUniPercent: Yup.number().required("single uni percent is required"),
    multiEngPercent: Yup.number().required("multi eng percent is required"),
    multiUniPercent: Yup.number().required("multi uni percent is required"),
    transactionalPercent: Yup.number().required("transactional percent is required"),
    promoPercent: Yup.number().required("promo percent is required"),
    explicitPercent: Yup.number().required("explicit percent is required"),
    implicitPercent: Yup.number().required("implicit percent is required"),
    dltPercent: Yup.number().required("dlt percent is required"),
  });

  const methods = useForm({
    resolver: yupResolver(Schema),
    defaultValues: {
      singleEngPercent: 100,
      singleUniPercent: 100,
      multiEngPercent: 100,
      multiUniPercent: 100,
      transactionalPercent: 100,
      promoPercent: 100,
      explicitPercent: 100,
      implicitPercent: 100,
      dltPercent: 100,
      userTransactional: 0,
      userPromo: 0,
      userExplicit: 0,
      userImplicit: 0,
      userDlt: 0,
      userSingleEng: 0,
      userSingleUni: 0,
      userMultiEng: 0,
      userMultiUni: 0
    },
  });

  const { control, watch, setValue, handleSubmit, reset, formState: { errors } } = methods;

  const watchPercent = watch();

  const calc = (base, percent) =>
    base ? ((base * (percent || 0)) / 100).toFixed(2) : 0;
  const calcLimit = (base, percent) =>
    base ? Math.floor((base * (percent || 0)) / 100) : 0;



  const basePlan = selectedUser ? {
    singleEng: selectedUser.single_part_english_char_limit,
    singleUni: selectedUser.single_part_unicode_char_limit,
    multiEng: selectedUser.multi_part_english_char_limit,
    multiUni: selectedUser.multi_part_unicode_char_limit,
    transactional: selectedUser.transactional_sms_price,
    promo: selectedUser.promotional_sms_price,
    explicit: selectedUser.explicit_sms_price,
    implicit: selectedUser.implicit_sms_price,
    dlt: selectedUser.dlt_sms_price,
  } : {};


  useEffect(() => {
    if (selectedUser) {
      reset({
        singleEngPercent: 100,
        singleUniPercent: 100,
        multiEngPercent: 100,
        multiUniPercent: 100,
        transactionalPercent: 100,
        promoPercent: 100,
        explicitPercent: 100,
        implicitPercent: 100,
        dltPercent: 100,
        userTransactional: selectedUser.transactional_sms_price,
        userPromo: selectedUser.promotional_sms_price,
        userExplicit: selectedUser.explicit_sms_price,
        userImplicit: selectedUser.implicit_sms_price,
        userDlt: selectedUser.dlt_sms_price,
        userSingleEng: selectedUser.single_part_english_char_limit,
        userSingleUni: selectedUser.single_part_unicode_char_limit,
        userMultiEng: selectedUser.multi_part_english_char_limit,
        userMultiUni: selectedUser.multi_part_unicode_char_limit
      });
    }
  }, [selectedUser]);




  const handleFieldChange = (rowIndex, key, value) => {
    setRows((prevRows) => {
      const newRows = [...prevRows];
      newRows[rowIndex] = { ...newRows[rowIndex], [key]: value };
      return newRows;
    });
  };

  const onSubmit = async (data) => {
    try {
      const payload = {
        planId: selectedUser.plan_id,
        assignTo: user.userId,
        singlePartUnicodeCharPercent: data.singleEngPercent,
        singlePartEnglishCharPercent: data.singleUniPercent,
        multiPartUnicodeCharPercent: data.multiEngPercent,
        multiPartEnglishCharPercent: data.multiUniPercent,
        transactionalSmsPricePercent: data.transactionalPercent,
        promotionalSmsPricePercent: data.promoPercent,
        explicitSmsPricePercent: data.explicitPercent,
        implicitSmsPricePercent: data.implicitPercent,
        DLTSmsPricePercent: data.dltPercent
      };
      await axiosPostCall('assignSmsPlan', payload);
      renderSuccessMessage('Plan assigned successfully');
      setAssignUser(false);
      setForceLoad(true);
    } catch (error) {
      const errorMessage = error?.message;
      renderErrorMessage(errorMessage);
    }
  }

  const checkPlanAssignStatus = (planId) => {
    let count = false;
    assignedPlan.map(({ plan_id }) => {
      if (plan_id === planId) count = true;
      return null;
    });
    return count;
  };

  useEffect(() => {
    setRows(credits);
  }, [credits]);

  const ActionJsx = ({ id, align, value, row }) => {
    const planId = typeof row?.plan_id === 'string' ? row.plan_id : String(row?.plan_id);
    const onClickAssignPlan = async (row) => {
      setAssignUser(true);
      setSelectedUser(row);
    }

    return (
      <TableCell
        key={id}
        align={align}
        sx={{
          color: 'grey.400',
          borderBottom: '0.5px dashed',
          borderColor: 'grey.700',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <LoadingButton
            disabled={checkPlanAssignStatus(row?.plan_id)}
            onClick={() => onClickAssignPlan(row)}
            variant="outlined"
            size="small"
            color="primary"
          >
            Assign
          </LoadingButton>
        </Box>
      </TableCell>
    );
  };
  const PriceBlock = ({ label, value }) => (
    <Box sx={{ width: 80 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography fontWeight={600}>{value}</Typography>
    </Box>
  );



  const description = (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '18px',
          width: '650px',
          overflow: 'hidden',
          height: 'calc(68vh)',
          overflowY: 'scroll',
          scrollbarWidth: 'none',
        }}
      >
        <Stack spacing={2}>

          <Row>
            <FieldHeading>Single English Char Limit</FieldHeading>
            <PriceBlock label="Base Value" value={basePlan.singleEng} />
            <RHFTextField
              name="singleEngPercent"
              label="%"
              type="number"
              sx={{ width: 200 }}
              onChange={(e) => {
                setValue('singleEngPercent', e.target.value);
                setValue('userSingleEng', calcLimit(basePlan.singleEng, e.target.value));
              }}
              disabled={true}
            />
            <RHFTextField
              name="userSingleEng"
              label="User Value"
              type="number"
              sx={{ width: 200 }}
              onChange={(e) => {
                setValue('userSingleEng', e.target.value);
                const pct = basePlan.singleEng
                  ? parseFloat(((e.target.value / basePlan.singleEng) * 100).toFixed(2))
                  : 0;
                setValue('singleEngPercent', pct);
              }}
              disabled={true}
            />
          </Row>

          <Row>
            <FieldHeading>Single Unicode Char Limit</FieldHeading>
            <PriceBlock label="Base Value" value={basePlan.singleUni} />
            <RHFTextField
              name="singleUniPercent"
              label="%"
              type="number"
              sx={{ width: 200 }}
              onChange={(e) => {
                setValue('singleUniPercent', e.target.value);
                setValue('userSingleUni', calcLimit(basePlan.singleUni, e.target.value));
              }}
              disabled={true}
            />
            <RHFTextField
              name="userSingleUni"
              label="User Value"
              type="number"
              sx={{ width: 200 }}
              onChange={(e) => {
                setValue('userSingleUni', e.target.value);
                const pct = basePlan.singleUni
                  ? parseFloat(((e.target.value / basePlan.singleUni) * 100).toFixed(2))
                  : 0;
                setValue('singleUniPercent', pct);
              }}
              disabled={true}
            />
          </Row>

          <Row>
            <FieldHeading>Multi English Char Limit</FieldHeading>
            <PriceBlock label="Base Value" value={basePlan.multiEng} />
            <RHFTextField
              name="multiEngPercent"
              label="%"
              type="number"
              sx={{ width: 200 }}
              onChange={(e) => {
                setValue('multiEngPercent', e.target.value);
                setValue('userMultiEng', calcLimit(basePlan.multiEng, e.target.value));
              }}
              disabled={true}
            />
            <RHFTextField
              name="userMultiEng"
              label="User Value"
              type="number"
              sx={{ width: 200 }}
              onChange={(e) => {
                setValue('userMultiEng', e.target.value);
                const pct = basePlan.multiEng
                  ? parseFloat(((e.target.value / basePlan.multiEng) * 100).toFixed(2))
                  : 0;
                setValue('multiEngPercent', pct);
              }}
              disabled={true}
            />
          </Row>

          <Row>
            <FieldHeading>Multi Unicode Char Limit</FieldHeading>
            <PriceBlock label="Base Value" value={basePlan.multiUni} />
            <RHFTextField
              name="multiUniPercent"
              label="%"
              type="number"
              sx={{ width: 200 }}
              onChange={(e) => {
                setValue('multiUniPercent', e.target.value);
                setValue('userMultiUni', calcLimit(basePlan.multiUni, e.target.value));
              }}
              disabled={true}
            />
            <RHFTextField
              name="userMultiUni"
              label="User Value"
              type="number"
              sx={{ width: 200 }}
              onChange={(e) => {
                setValue('userMultiUni', e.target.value);
                const pct = basePlan.multiUni
                  ? parseFloat(((e.target.value / basePlan.multiUni) * 100).toFixed(2))
                  : 0;
                setValue('multiUniPercent', pct);
              }}
              disabled={true}
            />
          </Row>

          <Row>
            <FieldHeading>Transactional SMS Price (In Paisa)</FieldHeading>
            <PriceBlock label="Base Price" value={basePlan.transactional} />
            <RHFTextField
              name="transactionalPercent"
              label="%"
              type="number"
              sx={{ width: 200 }}
              onChange={(e) => {
                setValue('transactionalPercent', e.target.value);
                setValue('userTransactional', calc(basePlan.transactional, e.target.value));
              }}
            />
            <RHFTextField
              name="userTransactional"
              label="User Price"
              type="number"
              sx={{ width: 200 }}
              onChange={(e) => {
                setValue('userTransactional', e.target.value);
                const pct = basePlan.transactional
                  ? parseFloat(((e.target.value / basePlan.transactional) * 100).toFixed(2))
                  : 0;
                setValue('transactionalPercent', pct);
              }}
            />
          </Row>

          <Row>
            <FieldHeading>Promotional SMS Price (In Paisa)</FieldHeading>
            <PriceBlock label="Base Price" value={basePlan.promo} />
            <RHFTextField
              name="promoPercent"
              label="%"
              type="number"
              sx={{ width: 200 }}
              onChange={(e) => {
                setValue('promoPercent', e.target.value);
                setValue('userPromo', calc(basePlan.promo, e.target.value));
              }}
            />
            <RHFTextField
              name="userPromo"
              label="User Price"
              type="number"
              sx={{ width: 200 }}
              onChange={(e) => {
                setValue('userPromo', e.target.value);
                const pct = basePlan.promo
                  ? parseFloat(((e.target.value / basePlan.promo) * 100).toFixed(2))
                  : 0;
                setValue('promoPercent', pct);
              }}
            />
          </Row>

          <Row>
            <FieldHeading>Explicit SMS Price (In Paisa)</FieldHeading>
            <PriceBlock label="Base Price" value={basePlan.explicit} />
            <RHFTextField
              name="explicitPercent"
              label="%"
              type="number"
              sx={{ width: 200 }}
              onChange={(e) => {
                setValue('explicitPercent', e.target.value);
                setValue('userExplicit', calc(basePlan.explicit, e.target.value));
              }}
            />
            <RHFTextField
              name="userExplicit"
              label="User Price"
              type="number"
              sx={{ width: 200 }}
              onChange={(e) => {
                setValue('userExplicit', e.target.value);
                const pct = basePlan.explicit
                  ? parseFloat(((e.target.value / basePlan.explicit) * 100).toFixed(2))
                  : 0;
                setValue('explicitPercent', pct);
              }}
            />
          </Row>

          <Row>
            <FieldHeading>Implicit SMS Price (In Paisa)</FieldHeading>
            <PriceBlock label="Base Price" value={basePlan.implicit} />
            <RHFTextField
              name="implicitPercent"
              label="%"
              type="number"
              sx={{ width: 200 }}
              onChange={(e) => {
                setValue('implicitPercent', e.target.value);
                setValue('userImplicit', calc(basePlan.implicit, e.target.value));
              }}
            />
            <RHFTextField
              name="userImplicit"
              label="User Price"
              type="number"
              sx={{ width: 200 }}
              onChange={(e) => {
                setValue('userImplicit', e.target.value);
                const pct = basePlan.implicit
                  ? parseFloat(((e.target.value / basePlan.implicit) * 100).toFixed(2))
                  : 0;
                setValue('implicitPercent', pct);
              }}
            />
          </Row>

          <Row>
            <FieldHeading>DLT SMS Price (In Paisa)</FieldHeading>
            <PriceBlock label="Base Price" value={basePlan.dlt} />
            <RHFTextField
              name="dltPercent"
              label="%"
              type="number"
              sx={{ width: 200 }}
              onChange={(e) => {
                setValue('dltPercent', e.target.value);
                setValue('userDlt', calc(basePlan.dlt, e.target.value));
              }}
            />
            <RHFTextField
              name="userDlt"
              label="User Price"
              type="number"
              sx={{ width: 200 }}
              onChange={(e) => {
                setValue('userDlt', e.target.value);
                const pct = basePlan.dlt
                  ? parseFloat(((e.target.value / basePlan.dlt) * 100).toFixed(2))
                  : 0;
                setValue('dltPercent', pct);
              }}
            />
          </Row>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', mt: 3 }}>
            <Box
              sx={{
                width: '7.625rem',
                backgroundColor: '#212B36',
                borderRadius: '8px',
                p: '0.562rem',
                height: '2.375rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                pointerEvents: 'auto'
              }}
              onClick={handleSubmit(onSubmit)}
            >
              <Typography sx={{ fontWeight: 600, color: '#fff' }}>
                Submit
              </Typography>
            </Box>
          </Box>
        </Stack>
      </Box>
    </FormProvider>

  )

  return (
    <Box>
      <Typography
        id="modal-modal-title"
        variant="h6"
        component="h2"
        sx={{ mb: '-20px' }}
      // sx={{ color: 'grey.10' }}
      >
        Reseller Plans
      </Typography>
      <PaginationTable
        page={page}
        ActionJsx={ActionJsx}
        setPage={setPage}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
        loading={tableLoading}
        rows={rows}
        columns={columns}
        maxHeight={680}
        mapKey="planName"
        onRowFieldChange={handleFieldChange}
      />
      <CustomModal
        open={assignUser}
        title={"Sms Plan Assigning"}
        description={description}
        close={() => setAssignUser("")}
        // action={forceActions()}
        isCloseIcon={true}
      />
    </Box>
  );
};
